import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import { useSelector } from "react-redux";
import FormGroup from 'components/Form/FormGroup';
import { I18n, Translate } from 'react-redux-i18n';
import { Badge } from 'reactstrap';
import FlexGroup from 'components/FlexGroup';
import ListHeader from 'components/ListView/ListHeader';
import { headerToColumns } from 'components/ListView';
import {
    ADDITIONAL_FEE,
    SCOOTER_LIST,
    SCOOTER_MAP,
    TICKET_LIST,
    TICKET_ASSIGNMENT,
} from 'constants/routes';
import {
    FILTER_FEATURE_SCOOTER_LIST,
    FILTER_FEATURE_SCOOTER_MAP,
    FILTER_FEATURE_TICKET_LIST,
    FILTER_FEATURE_TICKET_ASSIGNMENT,
    FILTER_FEATURE_ADDITIONAL_FEE,
} from 'constants/filters';
import ScooterListHeaders from 'view/ScooterList/ListHeader';
import TicketListHeaders from 'view/Ticket/List/header';
import AdditionalFeeFeeHeaders from 'components/AdditionalFeeTableList/column/all';
import ScooterFilter from 'view/ScooterList/FilterBar';
import TicketFilter, { FILTER_CREATE_TIME } from 'view/Ticket/Filter';
import { DEFAULT_SCOOTER_LIST_PAYLOAD } from 'view/ScooterList';
import { DEFAULT_SCOOTER_MAP_PAYLOAD } from 'view/ScooterMap';
import { DEFAULT_TICKET_LIST_PAYLOAD } from 'view/Ticket';
import { DEFAULT_TICKET_ASSIGNMENT_PAYLOAD } from 'view/TicketAssignment';
import AdditionaFeeFilter, { CREATE_TIME } from 'view/AdditionalFee/Filter';
import { DEFAULT_ADDITIONAL_FEE_PAYLOAD } from 'view/AdditionalFee';


import './filter-criteria.scss';


export const CRITERIA_CONFIG = {
    [FILTER_FEATURE_SCOOTER_LIST]: {
        header: ScooterListHeaders,
        route: SCOOTER_LIST,
        defaultPayload: DEFAULT_SCOOTER_LIST_PAYLOAD,
    },
    [FILTER_FEATURE_SCOOTER_MAP]: {
        header: null,
        route: SCOOTER_MAP,
        defaultPayload: DEFAULT_SCOOTER_MAP_PAYLOAD,
    },
    [FILTER_FEATURE_TICKET_LIST]: {
        header: TicketListHeaders,
        route: TICKET_LIST,
        defaultPayload: DEFAULT_TICKET_LIST_PAYLOAD,
        blockedFilter: [FILTER_CREATE_TIME, 'create_from', 'create_to'],
    },
    [FILTER_FEATURE_TICKET_ASSIGNMENT]: {
        header: null,
        route: TICKET_ASSIGNMENT,
        defaultPayload: DEFAULT_TICKET_ASSIGNMENT_PAYLOAD,
    },
    [FILTER_FEATURE_ADDITIONAL_FEE]: {
        header: AdditionalFeeFeeHeaders,
        route: ADDITIONAL_FEE,
        defaultPayload: DEFAULT_ADDITIONAL_FEE_PAYLOAD,
        blockedFilter: [CREATE_TIME, 'create_from', 'create_to'],
    },
};


const DEFAULT_CRITERIA = { filter: {} };

const FilterCriteria = ({
    feature,
    criteria,
    defaultSelected,
    viewMode,
}) => {
    const containerStyle = classNames({
        'filter-criteria': true,
        'hide': feature !== undefined ? false : true,
        'view-mode': viewMode,
    });
    const i18n = useSelector(state => state.i18n);
    const [columns, setColumns] = useState([]);
    const payload = criteria.current ?? { sort: undefined, filter: {} };
    const { sort } = payload;
    const [hasInValidSort, setHasInValidSort] = useState(false);
    const [inValidFilters, setInValidFilters] = useState([]);
    const [inValidFilterOpts, setInValidFilterOpts] = useState([]);

    const handleSortChange = ({ field, order }) => {
        let result;
        if (viewMode) {
            return ;
        }
        else {
            if (field && order) {
                result = `${ field },${ order }`;
            }
            payload.sort = result;
            setHasInValidSort(false);
        }

    };

    const clearInvalidSort = () => {
        if (!viewMode) {
            payload.sort  = undefined;
            setHasInValidSort(false);
        }
    };

    const clearInvalidFilter = (key) => e => {
        if (!viewMode) {
            payload.filter[key] = undefined;
            const result = inValidFilters.filter(i => i.filterKey !== key);
            setInValidFilters(result);
        }
    };
    const clearInvalidFilterOpt = ({ filterKey, id, key }) => e => {
        if (!viewMode) {
            const validResult = payload.filter[filterKey].filter(item => item !== id);
            payload.filter[filterKey] = (validResult.length) ? validResult : undefined;
            const result = inValidFilterOpts.filter(i => i.key !== key);
            setInValidFilterOpts(result);
        }
    };

    useEffect(() => {
        let result = false;
        if (sort && columns?.length) {
            const [field] = sort.split(',') ?? [];
            const isMatch = columns.filter(({ key }) => field === key);
            result = isMatch.length ? false : true;
        }
        setHasInValidSort(result);
    }, [columns, sort]);

    const invalidSortStyle = classNames({
        'cell': true,
        'sort-invalid': true,
        'disabled': viewMode,
    });

    const invalidFilterStyle = classNames({
        'invalid-filter-item': true,
        'disabled': viewMode,
    });

    const getInvalidFilters = (filters, configFilters, blockedFilter = []) => {
        const configKeys = new Set(Object.keys(configFilters));
        const payloadKeys = new Set(Object.keys(filters));
        const uniqueKeys = [...payloadKeys].filter(key => {
            if(key.endsWith('_from') || key.endsWith('_to')) {
                const seg = key.split('_');
                seg.pop();
                const prefix = seg.join('_');
                return !configKeys.has(prefix);
            }
            return !configKeys.has(key);
        });
        const inValidKeys = uniqueKeys.filter(key => blockedFilter.indexOf(key) === -1);
        const result = Array.from(inValidKeys, x => ({
            filterKey: x,
        }));
        return result;
    };

    const handleFilterChange = (type, value = {}) => {
        const { inputSelect, from, to } = value;

        if (value.hasOwnProperty('inputSelect')) {
            payload.filter[type] = inputSelect ?? undefined;
        }
        else {
            payload.filter[`${type}_from`] = from ?? undefined;
            payload.filter[`${type}_to`] = to ?? undefined;
        }
    };

    const handleCurryFilterChange = type => value => {
        handleFilterChange(type, value);
    }

    useEffect(() => { }, [i18n]);

    useEffect(() => {
        let fieldList, criteriaObj, invalidfilterList;
        const { invalid_criteria, ...seletedCriteria} = defaultSelected ?? {};
        const { header, defaultPayload = DEFAULT_CRITERIA, blockedFilter = [] } = CRITERIA_CONFIG[feature] ?? { ...DEFAULT_CRITERIA };

        if (feature !== undefined && header && header?.length) {
            fieldList = headerToColumns(header, true);

        }

        setColumns(fieldList);
        criteriaObj = {
            ...defaultPayload,
            size: undefined,
            filter: cloneDeep(defaultPayload?.filter ?? { }),
            sort: defaultPayload?.sort ?? undefined,
            ...cloneDeep(seletedCriteria),
        }
        criteria.current = criteriaObj;
        invalidfilterList = getInvalidFilters(criteriaObj.filter, defaultPayload.filter, blockedFilter);
        setInValidFilters(invalidfilterList);
        if (invalid_criteria) {
            const invalidCriteriaList = Object.entries(invalid_criteria).reduce((acc, [key, list])=> {
                const keyString = (key ==='assigned_to_id') ? I18n.t('ticket.assignee') : key;
                const flatCriteria = Array.from(list, x=> ({
                    key: `${key}${ x.name }`,
                    filterKey: key,
                    ...x,
                    displayName: `${ keyString }: ${ x.name }`,
                }));
                return acc.concat(flatCriteria);
            }, []);
            setInValidFilterOpts(invalidCriteriaList);
        }
    }, [feature, i18n, criteria, defaultSelected]);


    return (
        <FlexGroup className={ containerStyle }>
            {
                viewMode? (
                    <FlexGroup className="edit-hint"><Translate value="filters.criteria_can_not_edit" /></FlexGroup>
                ) : null
            }
            {
                columns?.length ? (
                    <FormGroup title={ I18n.t('filters.criteria_sorting') }>
                        <FlexGroup className="filter-criteria-sorting">
                            <ListHeader
                                onSort={ handleSortChange }
                                items={ columns ?? [] }
                                defaultSorted={ sort }
                                disabled={ viewMode }
                            />
                            {
                                (sort && hasInValidSort) ? (
                                    <span className={ invalidSortStyle } onClick={ clearInvalidSort }>
                                        { sort || '' }
                                        <Badge color="danger">
                                            <Translate value="filters.invalid" />
                                        </Badge>
                                    </span>
                                ) : null
                            }
                        </FlexGroup>
                    </FormGroup>
                ) : null
            }
            <FormGroup title={ I18n.t('filters.criteria_filters') }>
                <FlexGroup className="filter-criteria-filters">
                    {
                        (inValidFilters || inValidFilterOpts) ? (
                            <FlexGroup className="invalid-filters-section">
                                {
                                    inValidFilters.map(item => (
                                        <FlexGroup
                                            key={ item.filterKey }
                                            className={ invalidFilterStyle}
                                            onClick={ clearInvalidFilter(item.filterKey) }
                                        >
                                            <span>{ item.filterKey }</span>
                                            <Badge color="danger"><Translate value="filters.invalid" /></Badge>
                                        </FlexGroup>
                                    ))
                                }
                                {
                                    inValidFilterOpts.map(item => (
                                        <FlexGroup
                                            key={ item.key }
                                            className={ invalidFilterStyle}
                                            onClick={ clearInvalidFilterOpt(item) }
                                        >
                                            <span>{ item.displayName }</span>
                                            <Badge color="danger"><Translate value="filters.invalid" /></Badge>
                                        </FlexGroup>
                                    ))
                                }
                            </FlexGroup>
                        ) : null
                    }
                    {
                        (feature === FILTER_FEATURE_SCOOTER_LIST) ? (
                            <ScooterFilter
                                defaultSettings={ payload }
                                onChange={ handleFilterChange }
                                isForcedOpen
                                disabled={ viewMode }
                            />
                        ) : null
                    }
                    {
                        (feature === FILTER_FEATURE_SCOOTER_MAP) ? (
                            <ScooterFilter
                                defaultSettings={ payload }
                                onChange={ handleFilterChange }
                                isForcedOpen
                                disabled={ viewMode }
                            />
                        ) : null
                    }
                    {
                        (feature === FILTER_FEATURE_TICKET_LIST) ? (
                            <TicketFilter
                                filters={ payload.filter }
                                onChange={ handleCurryFilterChange }
                                excludeList={ [FILTER_CREATE_TIME] }
                                isForcedOpen
                                disabled={ viewMode }
                            />
                        ) : null
                    }
                    {
                        (feature === FILTER_FEATURE_TICKET_ASSIGNMENT) ? (
                            <TicketFilter
                                filters={ payload.filter }
                                onChange={ handleCurryFilterChange }
                                excludeList={ [FILTER_CREATE_TIME] }
                                isForcedOpen
                                disabled={ viewMode }
                            />
                        ) : null
                    }
                    {
                        (feature === FILTER_FEATURE_ADDITIONAL_FEE) ? (
                            <AdditionaFeeFilter
                                applyFilter={ handleCurryFilterChange }
                                filter={ payload.filter }
                                excludeList={ [CREATE_TIME] }
                                isForcedOpen
                                disabled={ viewMode }
                            />
                        ) : null
                    }
                </FlexGroup>
            </FormGroup>
        </FlexGroup>
    );
};

FilterCriteria.propTypes = {
    feature: PropTypes.number,
    criteria: PropTypes.shape({
        current: PropTypes.shape({
            sort: PropTypes.string,
            filter: PropTypes.shape({}),
        })
    }).isRequired,
    defaultSelected: PropTypes.shape({
        sort: PropTypes.string,
        filter: PropTypes.shape({}),
        invalid_criteria: PropTypes.shape({}),
    }),
    viewMode: PropTypes.bool,
};
FilterCriteria.defaultProps = {
    feature: undefined,
    defaultSelected: null,
    viewMode: false,
};

export default React.memo(FilterCriteria);
